import {firebaseConfig} from '../lib/firebase';
import {resolveQuestionContent} from '../features/practice/questionContentStorage';
import {getWorkbookSheets, spreadsheetToText} from '../components/editors/workbookModel';

export const EVALUATION_MATERIAL_LIMITS = {images:12,imageBytes:4*1024*1024,totalImageBytes:12*1024*1024,textCharacters:500000};
const supportedTypes = new Set(['image/png','image/jpeg','image/webp']);
const error = (code,message) => Object.assign(new Error(message),{code:'evaluation-material-'+code});
const unsupported = message => error('unsupported',message+' Your saved answer needs coordinator review; no automatic mark has been awarded.');
const checkAbort = signal => {if(signal?.aborted)throw error('unavailable','Preparing the answer timed out. Retry evaluation; the saved answer is unchanged.');};

// Images are read by this browser, then sent as bytes. Never ask the backend to
// download an arbitrary URL supplied by a student or an imported question.
export function isEvaluationStorageUrl(value) {
    try {
        const url=new URL(value),buckets=new Set([firebaseConfig.storageBucket,`${firebaseConfig.projectId}.appspot.com`]);
        if(url.protocol!=='https:' || url.username || url.password || url.port)return false;
        if(url.hostname==='firebasestorage.googleapis.com') {
            const match=/^\/v0\/b\/([^/]+)\/o\/(.+)$/.exec(url.pathname);
            return Boolean(match && buckets.has(decodeURIComponent(match[1])) && decodeURIComponent(match[2]));
        }
        if(url.hostname==='storage.googleapis.com') {
            const match=/^\/([^/]+)\/(.+)$/.exec(url.pathname);
            return Boolean(match && buckets.has(decodeURIComponent(match[1])) && match[2]);
        }
        return false;
    } catch {return false;}
}

async function download(source,maximum,signal) {
    if(!isEvaluationStorageUrl(source))throw unsupported('An image or document uses an unsupported external link. Ask the coordinator to upload it to platform storage.');
    checkAbort(signal);
    let response;
    try {response=await fetch(source,{signal,redirect:'error',credentials:'omit'});} catch {throw error('unavailable','An assessment image or document could not be downloaded. Check your connection and retry evaluation.');}
    if(!response.ok)throw error('unavailable','An assessment image or document is unavailable. Ask the coordinator to check the uploaded file, then retry evaluation.');
    if(Number(response.headers?.get('content-length'))>maximum) {
        await response.body?.cancel?.().catch(()=>{});
        throw error('too-large','An assessment file is too large for automatic marking. The coordinator can review the saved answer.');
    }
    const type=(response.headers?.get('content-type') || '').split(';')[0].toLowerCase();
    let bytes;
    if(response.body?.getReader) {
        const reader=response.body.getReader(),chunks=[];let size=0;
        try {
            while(true) {
                checkAbort(signal);
                const next=await reader.read();if(next.done)break;
                size+=next.value.byteLength;
                if(size>maximum)throw error('too-large','An assessment file is too large for automatic marking. The coordinator can review the saved answer.');
                chunks.push(next.value);
            }
            bytes=new Uint8Array(size);let offset=0;chunks.forEach(chunk=>{bytes.set(chunk,offset);offset+=chunk.length;});
        } catch(problem) {
            await reader.cancel().catch(()=>{});
            if(problem.code?.startsWith?.('evaluation-material-'))throw problem;
            throw error('unavailable','An assessment file download was interrupted. Check your connection and retry evaluation.');
        } finally {reader.releaseLock();}
    } else {
        try {bytes=new Uint8Array(await response.arrayBuffer());}
        catch {throw error('unavailable','An assessment file download was interrupted. Check your connection and retry evaluation.');}
        if(bytes.length>maximum)throw error('too-large','An assessment file is too large for automatic marking. The coordinator can review the saved answer.');
    }
    checkAbort(signal);
    return {bytes,type};
}
const bytesToBase64 = bytes => {
    let binary='';
    for(let offset=0;offset<bytes.length;offset+=8192)binary+=String.fromCharCode(...bytes.subarray(offset,offset+8192));
    return btoa(binary);
};
function decodeImage(source) {
    const match=/^data:([^;,]+);base64,([\s\S]*)$/i.exec(source);
    if(!match || !supportedTypes.has(match[1].toLowerCase()))throw unsupported('This image format cannot be read automatically. Upload a PNG, JPEG or WebP image.');
    const data=match[2].replace(/\s/g,''),maximum=EVALUATION_MATERIAL_LIMITS.imageBytes;
    if(data.length>Math.ceil(maximum/3)*4)throw error('too-large','An image exceeds the 4 MB automatic marking limit. The coordinator can review the saved answer.');
    if(!data || data.length%4 || /[^A-Za-z0-9+/=]/.test(data) || !/^[^=]*={0,2}$/.test(data))throw error('unavailable','An assessment image is damaged or incomplete. Ask the coordinator to replace it before retrying.');
    let bytes;
    try {bytes=atob(data).length;} catch {throw error('unavailable','An assessment image could not be read. Ask the coordinator to replace it before retrying.');}
    if(bytes>maximum)throw error('too-large','An image exceeds the 4 MB automatic marking limit. The coordinator can review the saved answer.');
    return {mimeType:match[1].toLowerCase(),data,bytes};
}

function spreadsheet(value) {
    if(!value)return '';
    if(getWorkbookSheets(value).some(({data})=>data.drawings?.strokes?.length))throw unsupported('This spreadsheet contains handwriting or drawn working that requires visual review.');
    try {return spreadsheetToText(value);} catch {throw error('unavailable','The saved spreadsheet could not be read completely. The coordinator can review the original answer.');}
}

// Preserve every cell and formula across every sheet. Rich text is converted to
// readable text and image parts, never huge HTML/base64 tokens in the prompt.
export async function buildEvaluationMaterial(question,answer,{scenario,signal}={}) {
    checkAbort(signal);
    const hydrate=async value=>{
        if(!value?.contentStorage)return value || {};
        if(!isEvaluationStorageUrl(value.contentStorage.url))throw unsupported('The full question uses an unsupported storage link.');
        try {const resolved=await resolveQuestionContent(value);checkAbort(signal);return resolved;}
        catch(problem) {if(problem.code?.startsWith?.('evaluation-material-'))throw problem;throw error('unavailable','The full question or marking material could not be loaded. Retry evaluation after checking the uploaded file.');}
    };
    const q=await hydrate(question),context=typeof scenario==='object' && scenario?await hydrate(scenario):scenario;
    const images=[],loaded=new Map(),references=new Map();let totalImageBytes=0;
    const addImage=async(source,role)=>{
        source=String(source || '').trim();
        if(!source)throw error('unavailable','An assessment image has no file link. Ask the coordinator to check the uploaded material.');
        const key=role+'\n'+source;if(references.has(key))return references.get(key);
        if(images.length>=EVALUATION_MATERIAL_LIMITS.images)throw error('too-large','This answer and its marking material contain more than 12 images. The coordinator can review the saved answer.');
        let image=loaded.get(source);
        if(!image) {
            if(/^data:/i.test(source))image=decodeImage(source);
            else {
                const file=await download(source,EVALUATION_MATERIAL_LIMITS.imageBytes,signal);
                if(!supportedTypes.has(file.type))throw unsupported('An assessment image is not a PNG, JPEG or WebP file.');
                if(!file.bytes.length)throw error('unavailable','An assessment image is empty. Ask the coordinator to replace it.');
                image={mimeType:file.type,data:bytesToBase64(file.bytes),bytes:file.bytes.length};
            }
            loaded.set(source,image);
        }
        totalImageBytes+=image.bytes;
        if(totalImageBytes>EVALUATION_MATERIAL_LIMITS.totalImageBytes)throw error('too-large','Assessment images exceed the 12 MB automatic marking limit. The coordinator can review the saved answer.');
        images.push({role,mimeType:image.mimeType,data:image.data});
        const label=`[${role} image ${images.length}]`;references.set(key,label);return label;
    };
    const richText=async(value,role,imageOnly=false)=>{
        checkAbort(signal);
        if(value==null || value==='')return '';
        if(Array.isArray(value)) {const parts=[];for(const item of value)parts.push(await richText(item,role,imageOnly));return parts.filter(Boolean).join('\n');}
        if(typeof value!=='string')throw unsupported('Part of the assessment material has an unreadable format.');
        let source=value.trim();if(!source)return '';
        if(source.length>28*1024*1024)throw error('too-large','An assessment document is too large for automatic marking. The complete saved answer needs coordinator review.');
        if(source.startsWith('STORAGE_URL:')) {
            const file=await download(source.slice(12),20*1024*1024,signal);
            source=new TextDecoder().decode(file.bytes);
        }
        if(imageOnly || /^data:image\//i.test(source) || isEvaluationStorageUrl(source))return addImage(source,role);
        const doc=new DOMParser().parseFromString(source,'text/html');
        doc.querySelectorAll('script,style,iframe,object,embed,link,meta,base,template').forEach(node=>node.remove());
        // There is no lossless bitmap exporter for the editor's potentially very
        // long ink surface. Do not infer a mark from an annotation placeholder.
        if(doc.querySelector('svg path,svg line,svg rect,svg circle,svg ellipse,svg polygon,svg polyline,svg text,svg image,svg use,svg foreignObject,canvas'))throw unsupported('This answer or marking material contains handwriting or drawings that require visual review.');
        for(const node of doc.querySelectorAll('[data-instil-ink]')) {
            try {if(JSON.parse(decodeURIComponent(node.getAttribute('data-instil-ink')))?.strokes?.length)throw unsupported('This answer contains handwriting that requires visual review.');}
            catch(problem) {if(problem.code)throw problem;throw unsupported('Saved handwriting could not be read.');}
            node.remove();
        }
        for(const node of doc.querySelectorAll('img'))node.replaceWith(doc.createTextNode(await addImage(node.getAttribute('src'),role)));
        doc.querySelectorAll('svg').forEach(node=>node.remove());
        const textOf=node=>{
            if(node.nodeType===3)return node.nodeValue;
            if(node.nodeName==='BR')return '\n';
            const text=Array.from(node.childNodes).map(textOf).join('');
            return text+(/^(TD|TH)$/.test(node.nodeName)?'\t':/^(P|DIV|LI|H[1-6]|TR|TABLE|UL|OL)$/.test(node.nodeName)?'\n':'');
        };
        return textOf(doc.body).replace(/\u00a0/g,' ').replace(/[ \t]+\n/g,'\n').replace(/\n{3,}/g,'\n\n').trim();
    };
    const word=await richText(typeof answer==='string'?answer:answer?.wordContent,'student');
    const sheet=spreadsheet(answer?.excelData);
    const studentAnswer=[word?`Written response:\n${word}`:'',sheet?`Spreadsheet working:\n${sheet}`:''].filter(Boolean).join('\n\n');
    if(!studentAnswer.trim())throw error('unavailable','No readable student answer was found. The coordinator can review the saved response.');
    const model=await richText(q.suggestedAnswerContent,'model',q.suggestedAnswerFormat==='image');
    const modelSheet=spreadsheet(q.suggestedAnswerExcel);
    const suggestedAnswer=[model,modelSheet?`Model spreadsheet:\n${modelSheet}`:''].filter(Boolean).join('\n\n');
    const markingScheme=await richText(q.markingSchemeContent,'rubric',q.markingSchemeFormat==='image');
    const questionParts=[];
    if(context) {
        const scenarioText=await richText(typeof context==='string'?context:context.text,'question');
        const scenarioImages=typeof context==='object'?await richText(context.images,'question',true):'';
        if(scenarioText || scenarioImages)questionParts.push(`Shared scenario:\n${[scenarioText,scenarioImages].filter(Boolean).join('\n')}`);
    }
    questionParts.push(await richText(q.text,'question'),await richText(q.images,'question',true));
    const questionText=questionParts.filter(Boolean).join('\n\n');
    checkAbort(signal);
    if([studentAnswer,suggestedAnswer,markingScheme,questionText].reduce((sum,text)=>sum+text.length,0)>EVALUATION_MATERIAL_LIMITS.textCharacters)throw error('too-large','Assessment text exceeds the automatic marking limit. The complete saved answer needs coordinator review.');
    return {studentAnswer,suggestedAnswer,markingScheme,questionText,images};
}
